/*
 * Based on code from
 *
 * Copyright (c) 2017 Ken Hibino.
 * Licensed under the MIT License (MIT).
 * See https://kenny-hibino.github.io/react-places-autocomplete
 */

/* eslint-disable */
/* global google:true*/
import { Component } from 'react';
import FormikControl from '../FormikContainer/FormikControl';
import cx from 'classnames';
import PropTypes from 'prop-types';
import Spinner from 'react-spinkit';
import styles from '../../SCSS/PlacesAutocomplete.module.scss';

interface AutocompleteItem {
  suggestion: string;
  placeId: string;
  active: boolean;
  index: number;
  formattedSuggestion: {
    mainText: string;
    secondaryText: string;
  };
}

function isSchool(types: string[]) {
  return types.indexOf('school') > -1 || types.indexOf('point_of_interest') > -1;
}

function matchAddressComponent(
  components: google.maps.GeocoderAddressComponent[] | undefined,
  desiredComponent: string,
) {
  if (components) {
    const matchingComponent = components.filter((component) => component.types.indexOf(desiredComponent) > -1);

    if (matchingComponent.length > 0) {
      return matchingComponent[0].long_name;
    }
  }
  return '';
}

export function getAddressComponents(
  place: google.maps.places.PlaceResult,
  status: google.maps.places.PlacesServiceStatus,
) {
  const output: Record<string, any> = {};
  if (status === google.maps.places.PlacesServiceStatus.OK) {
    output.country = matchAddressComponent(place.address_components, 'country');
    try {
      output.postal_code = matchAddressComponent(place.address_components, 'postal_code');
    } catch (err) {
      output.postal_code = '';
    }
    if (output.country === 'South Korea') {
      output.country = 'Korea, Republic of';
    }

    // get address
    const streetNumber = matchAddressComponent(place.address_components, 'street_number');
    const route = matchAddressComponent(place.address_components, 'route');
    if (streetNumber && route) {
      output.address = `${streetNumber} ${route}`;
    } else if (place.vicinity) {
      output.address = place.vicinity;
    } else {
      output.address = place.formatted_address;
    }

    output.state = matchAddressComponent(place.address_components, 'administrative_area_level_1');
    try {
      output.city = matchAddressComponent(place.address_components, 'locality');
    } catch (err) {
      try {
        output.city = matchAddressComponent(place.address_components, 'postal_town');
      } catch (err) {
        output.city = output.state;
      }
    }
    if (place.types) {
      output.isSchool = isSchool(place.types);
    }
  }

  return output;
}

interface Props {
  inputProps: {
    name: string;
    value: string;
    onChange: (schoolNameOrAddress: string) => void;
    type: string;
    placeholder: string;
    autoFocus: boolean;
    autoComplete: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  };
  autocompleteItem: ({
    formattedSuggestion,
  }: {
    formattedSuggestion: {
      mainText: string;
      secondaryText: string;
    };
  }) => JSX.Element;
  classNames: { root: string; input: string };
  onSelect: (address: string, place: string) => void;
  onError: (status: string) => void;
  onShowList: (showStatus: boolean) => void;
  onEnterKeyDown: (inputSchoolName: string) => void;
  clearItemsOnError: boolean;
  validate: (value: string) => boolean;
  styles: {
    autocompleteItemActive: {
      backgroundColor: string;
    };
  };
  options?: Record<string, unknown>;
}
interface State {
  isShowSpinner: boolean;
  isShowHint: boolean;
  autocompleteItems: AutocompleteItem[];
  hintText: string;
}
class PlacesAutocomplete extends Component<Props, State> {
  autocompleteService?: google.maps.places.AutocompleteService;
  autocompleteOK?: google.maps.places.PlacesServiceStatus.OK;
  zeroResults?: google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
  constructor(props: Props) {
    super(props);

    this.state = {
      isShowSpinner: false,
      isShowHint: false,
      autocompleteItems: [] as AutocompleteItem[],
      hintText: 'This is hint text',
    };

    this.autocompleteCallback = this.autocompleteCallback.bind(this);
    this.handleInputKeyDown = this.handleInputKeyDown.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  componentDidMount() {
    if (!window.google) {
      throw new Error(
        'Google Maps JavaScript API library must be loaded. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library',
      );
    }

    if (!window.google.maps.places) {
      throw new Error(
        'Google Maps Places library must be loaded. Please add `libraries=places` to the src URL. See: https://github.com/kenny-hibino/react-places-autocomplete#load-google-library',
      );
    }

    this.autocompleteService = new google.maps.places.AutocompleteService();
    this.autocompleteOK = google.maps.places.PlacesServiceStatus.OK;
    this.zeroResults = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
  }

  autocompleteCallback(
    predictions: google.maps.places.AutocompletePrediction[] | null,
    status: google.maps.places.PlacesServiceStatus,
  ) {
    if (status === this.zeroResults) {
      this.setState({
        isShowSpinner: false,
        isShowHint: true,
        hintText: 'No matches found',
      });
      return;
    }

    if (status != this.autocompleteOK) {
      this.setState({
        isShowSpinner: false,
        isShowHint: true,
        hintText: 'There was an error',
      });
      this.props.onError(status);
      if (this.props.clearItemsOnError) {
        this.clearAutocomplete();
      }
      return;
    }

    // transform snake_case to camelCase
    const formattedSuggestion = (structured_formatting: google.maps.places.StructuredFormatting) => ({
      mainText: structured_formatting.main_text,
      secondaryText: structured_formatting.secondary_text,
    });

    // filter out schools and addresses
    const service = new google.maps.places.PlacesService(document.createElement('div'));
    const calls: Promise<string[]>[] = [];

    predictions!.map((p) => {
      calls.push(getPlaceTypes(p));
    });

    Promise.all(calls).then(
      (outputArray) => {
        this.setState({ isShowSpinner: false });

        predictions!.map((p, i) => {
          p.types = p.types.concat(outputArray[i]);
        });

        const newp = predictions!.filter((p) => isInclude(p.types));

        if (newp.length) {
          this.setState({
            autocompleteItems: newp.map((p, idx) => ({
              suggestion: p.description,
              placeId: p.place_id,
              active: false,
              index: idx,
              formattedSuggestion: formattedSuggestion(p.structured_formatting),
            })),
            isShowHint: true,
            hintText: 'Select school then hit <Enter> when done',
          });

          return;
        }

        this.setState({ hintText: 'No matches found yet', isShowHint: true });
      },
      (errorStatus) => {
        console.warn(`PlacesAutocomplete: using unfiltered list - errorStatus ${errorStatus}`);
        this.setState({
          autocompleteItems: predictions!.map((p, idx) => ({
            suggestion: p.description,
            placeId: p.place_id,
            active: false,
            index: idx,
            formattedSuggestion: formattedSuggestion(p.structured_formatting),
          })),
          isShowSpinner: false,
        });
      },
    );

    function isInclude(types: string[]) {
      if (types.indexOf('school') > -1) {
        return true;
      }

      if (types.indexOf('premise') > -1) {
        return true;
      }

      if (types.indexOf('street_address') > -1) {
        return true;
      }

      return false;
    }

    function getPlaceTypes(predictedObject: google.maps.places.AutocompletePrediction) {
      return new Promise<string[]>((resolve, reject) => {
        const request = { placeId: predictedObject.place_id };
        service.getDetails(request, (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(place!.types as string[]);
          } else {
            reject(status);
          }
        });
      });
    }
  }

  clearAutocomplete() {
    this.setState({ autocompleteItems: [] as AutocompleteItem[] });
  }

  selectAddress(address: string, placeId: string) {
    this.clearAutocomplete();
    this.handleSelect(address, placeId);
  }

  handleSelect(address: string, placeId: string) {
    this.props.onSelect ? this.props.onSelect(address, placeId) : this.props.inputProps.onChange(address);
  }

  getActiveItem() {
    return this.state.autocompleteItems.find((item) => item.active);
  }

  selectActiveItemAtIndex(index: number) {
    const activeName = this.state.autocompleteItems.find((item) => item.index === index).suggestion;
    this.setActiveItemAtIndex(index);
    this.props.inputProps.onChange(activeName);
  }

  handleEnterKey() {
    this.setState({ isShowHint: false });
    const activeItem = this.getActiveItem();
    if (activeItem === undefined) {
      this.handleEnterKeyWithoutActiveItem();
    } else {
      this.selectAddress(activeItem.suggestion, activeItem.placeId);
    }
  }

  handleEnterKeyWithoutActiveItem() {
    if (this.props.onEnterKeyDown) {
      this.props.onEnterKeyDown(this.props.inputProps.value);
      this.clearAutocomplete();
    } else {
      // noop
    }
  }

  handleDownKey() {
    if (this.state.autocompleteItems.length === 0) {
      return;
    }

    const activeItem = this.getActiveItem();
    if (activeItem === undefined) {
      this.selectActiveItemAtIndex(0);
    } else {
      const nextIndex = (activeItem.index + 1) % this.state.autocompleteItems.length;
      this.selectActiveItemAtIndex(nextIndex);
    }
  }

  handleUpKey() {
    if (this.state.autocompleteItems.length === 0) {
      return;
    }

    const activeItem = this.getActiveItem();
    if (activeItem === undefined) {
      this.selectActiveItemAtIndex(this.state.autocompleteItems.length - 1);
    } else {
      let prevIndex;
      if (activeItem.index === 0) {
        prevIndex = this.state.autocompleteItems.length - 1;
      } else {
        prevIndex = (activeItem.index - 1) % this.state.autocompleteItems.length;
      }
      this.selectActiveItemAtIndex(prevIndex);
    }
  }

  handleInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    this.setState({ isShowHint: false });

    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        this.handleEnterKey();
        break;
      case 'ArrowDown':
        event.preventDefault(); // prevent the cursor from moving
        this.handleDownKey();
        break;
      case 'ArrowUp':
        event.preventDefault(); // prevent the cursor from moving
        this.handleUpKey();
        break;
      case 'Escape':
        this.clearAutocomplete();
        break;
    }

    if (this.props.inputProps.onKeyDown) {
      this.props.inputProps.onKeyDown(event);
    }
  }

  setActiveItemAtIndex(index: number) {
    this.setState({
      autocompleteItems: this.state.autocompleteItems.map((item, idx) => {
        if (idx === index) {
          return { ...item, active: true };
        }
        return { ...item, active: false };
      }),
    });
  }

  predictionDebounceTimeout: NodeJS.Timeout | null = null;
  predictionSeedValue: string | null = null;

  handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.inputProps.onChange(event.target.value);
    if (!event.target.value) {
      this.clearAutocomplete();
      return;
    }

    if (event.target.value.length > 5) {
      this.predictionSeedValue = event.target.value;

      clearTimeout(this.predictionDebounceTimeout as NodeJS.Timeout);

      this.predictionDebounceTimeout = setTimeout(() => {
        this.setState({ isShowSpinner: true, isShowHint: false });
        this.autocompleteService?.getPlacePredictions(
          { ...this.props.options, input: this.predictionSeedValue as string },
          this.autocompleteCallback,
        );
      }, 500);
    }
  }

  handleInputOnBlur(event: React.FocusEvent<HTMLInputElement>) {
    if (this.props.inputProps.onBlur) {
      this.props.inputProps.onBlur(event);
    }

    this.handleEnterKey();
    this.clearAutocomplete();
  }

  getInputProps() {
    const defaultInputProps = {
      type: 'text',
    };

    return {
      ...defaultInputProps,
      ...this.props.inputProps,
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        this.handleInputChange(event);
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
        this.handleInputKeyDown(event);
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        this.handleInputOnBlur(event);
      },
    };
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { autocompleteItems } = this.state;
    if (prevState.autocompleteItems.length !== autocompleteItems.length) {
      this.props.onShowList(autocompleteItems.length > 0);
    }
  }

  render() {
    const { autocompleteItems } = this.state;
    const inputProps = this.getInputProps();

    return (
      <div className={styles.root}>
        <FormikControl control='input' label='Your School Name*' {...inputProps} />
        <div
          className={cx(styles.spinnerContainer, {
            [`${styles.showSpinner}`]: this.state.isShowSpinner,
          })}
        >
          <Spinner fadeIn='none' name='ball-spin-fade-loader' />
        </div>
        <div
          className={cx(styles.hintContainer, {
            [`${styles.showHint}`]: this.state.isShowHint,
          })}
        >
          {this.state.hintText}
        </div>
        {autocompleteItems.length > 0 && (
          <div className={styles.autocompleteContainer}>
            {autocompleteItems.map((p, idx) => (
              <div
                key={p.placeId}
                className={
                  p.active ? cx(styles.autocompleteItem, styles.autocompleteItemActive) : styles.autocompleteItem
                }
                onMouseOver={() => this.setActiveItemAtIndex(p.index)}
                onMouseDown={() => this.selectAddress(p.suggestion, p.placeId)}
              >
                {this.props.autocompleteItem({
                  formattedSuggestion: p.formattedSuggestion,
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

PlacesAutocomplete.propTypes = {
  inputProps: (props, propName) => {
    const inputProps = props[propName];

    if (!inputProps.hasOwnProperty('value')) {
      throw new Error("'inputProps' must have 'value'.");
    }

    if (!inputProps.hasOwnProperty('onChange')) {
      throw new Error("'inputProps' must have 'onChange'.");
    }
  },
  onError: PropTypes.func,
  clearItemsOnError: PropTypes.bool,
  onSelect: PropTypes.func,
  autocompleteItem: PropTypes.func,
  options: PropTypes.shape({
    bounds: PropTypes.object,
    componentRestrictions: PropTypes.object,
    location: PropTypes.object,
    offset: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    types: PropTypes.array,
  }),
};

PlacesAutocomplete.defaultProps = {
  clearItemsOnError: false,
  onError: (status: string) =>
    console.error(
      '[react-places-autocomplete]: error happened when fetching data from Google Maps API.\nPlease check the docs here (https://developers.google.com/maps/documentation/javascript/places#place_details_responses)\nStatus: ',
      status,
    ),
  autocompleteItem: ({ suggestion }: { suggestion: string }) => <div>{suggestion}</div>,
  options: {},
};

export default PlacesAutocomplete;
